<template>
  <div class="thread-view">
    <div v-if="conversation" class="thread-view__header">
      <v-avatar
        v-if="conversation.otherUser.photoUrl"
        :image="conversation.otherUser.photoUrl"
        size="50"
      ></v-avatar>
      <v-avatar v-else color="primary" size="50">
        {{ initials(conversation.otherUser.name) }}
      </v-avatar>
      <div class="ml-3">
        <div class="font-weight-bold">{{ conversation.otherUser.name }}</div>
        <v-chip size="x-small" variant="outlined" class="product-chip">{{
          productLabel
        }}</v-chip>
      </div>
      <!-- Schedule (student) / Actions menu (provider) lands here as a
           follow-up - ThreadHeaderActions. -->
    </div>

    <div ref="scrollArea" class="thread-view__messages" @scroll="onScroll">
      <div v-if="loadingEarlier" class="text-center mb-3">
        <v-progress-circular
          indeterminate
          size="20"
          width="2"
          color="primary"
        ></v-progress-circular>
      </div>

      <template v-for="item in timelineItems">
        <div
          v-if="item.type === 'divider'"
          :key="`divider-${item.key}`"
          class="thread-view__date-divider"
        >
          {{ item.label }}
        </div>

        <div
          v-else-if="item.type === 'new-divider'"
          :key="`new-divider-${item.key}`"
          class="thread-view__new-divider"
          :class="{ 'thread-view__new-divider--fading': dividerFading }"
        >
          New messages
        </div>

        <div
          v-else
          :key="`group-${item.key}`"
          class="thread-view__group"
          :class="{ 'thread-view__group--self': item.isSelf }"
        >
          <v-avatar
            v-if="!item.isSelf"
            size="28"
            class="thread-view__group-avatar"
          >
            <v-img
              v-if="conversation.otherUser.photoUrl"
              :src="conversation.otherUser.photoUrl"
            ></v-img>
            <span v-else>{{ initials(conversation.otherUser.name) }}</span>
          </v-avatar>

          <div class="thread-view__group-body">
            <div
              v-for="message in item.messages"
              :key="message.id"
              class="thread-view__bubble"
              :class="{ 'thread-view__bubble--self': item.isSelf }"
            >
              <div>{{ message.body }}</div>
              <a
                v-for="attachment in message.attachments"
                :key="attachment.id"
                :href="attachment.url"
                target="_blank"
                rel="noopener"
                class="thread-view__attachment"
              >
                <span class="thread-view__attachment-icon">
                  <v-icon size="14">{{
                    attachmentIcon(attachment.fileType)
                  }}</v-icon>
                </span>
                <span class="thread-view__attachment-filename">{{
                  attachment.filename
                }}</span>
              </a>
            </div>

            <div class="thread-view__timestamp">
              {{
                formatTime(item.messages[item.messages.length - 1].createdAt)
              }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <RelationshipInactiveNotice
      v-if="isInactive"
      :product="conversation.product"
    />
    <MessageComposer
      v-else-if="composerReady"
      :key="conversationToken"
      :conversation-token="conversationToken"
      :draft="draft"
      @sent="onSent"
      @typing="onTyping"
    />
  </div>
</template>

<script>
import moment from "moment";
import messagingApi from "@/services/messagingApi";
import useInboxState from "@/composables/useInboxState";
import MessageComposer from "@/components/messaging/MessageComposer.vue";
import RelationshipInactiveNotice from "@/components/messaging/RelationshipInactiveNotice.vue";

const inbox = useInboxState();

export default {
  name: "ThreadView",
  components: { MessageComposer, RelationshipInactiveNotice },
  data() {
    return {
      inbox,
      conversation: null,
      messages: [],
      draft: null,
      hasMore: false,
      nextBefore: null,
      loadingEarlier: false,
      newMessagesDividerId: null,
      dividerFading: false,
      lastKnownMessageId: null,
      polling: false,
      loadedToken: null
    };
  },
  computed: {
    conversationToken() {
      return this.inbox.state.activeConversationToken;
    },
    currentUserId() {
      return this.$store.state.user?.id;
    },
    productLabel() {
      return this.conversation?.product === "uthrive"
        ? "Basic Needs"
        : "Therapy";
    },
    // Flattens messages into date dividers + consecutive-same-sender groups,
    // so the template renders one avatar/timestamp per group rather than
    // per message (matches the mock — a burst of replies isn't repeated
    // chrome per bubble).
    timelineItems() {
      const items = [];
      let currentGroup = null;

      this.messages.forEach((message, index) => {
        if (this.showDateDivider(index)) {
          items.push({
            type: "divider",
            key: message.id,
            label: this.formatDateDivider(message.createdAt)
          });
          currentGroup = null;
        }

        if (message.id === this.newMessagesDividerId) {
          items.push({ type: "new-divider", key: message.id });
          currentGroup = null;
        }

        const self = this.isSelf(message);
        if (!currentGroup || currentGroup.isSelf !== self) {
          currentGroup = {
            type: "group",
            key: message.id,
            isSelf: self,
            messages: []
          };
          items.push(currentGroup);
        }
        currentGroup.messages.push(message);
      });

      return items;
    },
    // Trust the backend's RelationshipStatus — it already covers both
    // products (case-closed for uthrive, 12-month/live window for umatch)
    // in one place, so no need to duplicate that rule here.
    isInactive() {
      return !!this.conversation && !this.conversation.relationshipActive;
    },
    composerReady() {
      return !!this.conversation && this.loadedToken === this.conversationToken;
    }
  },
  watch: {
    conversationToken: {
      immediate: true,
      handler(token) {
        if (token) this.load();
      }
    }
  },
  mounted() {
    // Fresh polling loop, deliberately not adapted from the Basic Needs
    // MessageContainer.vue pattern - an open thread reflects new messages
    // without a manual refresh. poll() checks the cheap peek endpoint
    // first (no audit-log write), only paying for the real ThreadReader
    // fetch when something's actually new.
    this.pollTimer = setInterval(this.poll, 30000);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    clearTimeout(this.dividerFadeTimer);
  },
  methods: {
    async load() {
      const token = this.conversationToken;
      const { data } = await messagingApi.fetchThread(token);
      this.conversation = data.conversation;
      this.messages = data.messages;
      this.draft = data.draft;
      this.hasMore = data.hasMore;
      this.nextBefore = data.nextBefore;
      this.newMessagesDividerId = null;
      this.lastKnownMessageId = data.messages.at(-1)?.id ?? null;
      this.loadedToken = token;
      this.scrollToBottom();
    },
    // Re-fetches the latest page and appends anything newer than what's
    // already shown - in place, below a "New messages" divider that's set
    // once and stays for the rest of this open session. Doesn't touch
    // scroll position unless the reader was already at the bottom, per PRD
    // ("without moving the user's scroll position if reading history").
    async poll() {
      if (this.polling || !this.conversationToken) return;

      this.polling = true;
      try {
        // Cheap check first - no audit-log write, no read-marker advance.
        // Only pay for the real (ThreadReader) fetch when there's actually
        // something new to show.
        const { data: peek } = await messagingApi.peekThread(
          this.conversationToken,
          this.lastKnownMessageId ?? 0
        );
        if (!peek.hasNew) return;

        const { data } = await messagingApi.fetchThread(this.conversationToken);
        this.conversation = data.conversation;

        const newMessages = data.messages.filter(
          m =>
            this.lastKnownMessageId === null || m.id > this.lastKnownMessageId
        );
        if (newMessages.length === 0) return;

        const wasAtBottom = this.isAtBottom();
        if (this.newMessagesDividerId === null) {
          this.newMessagesDividerId = newMessages[0].id;
        }
        this.messages = [...this.messages, ...newMessages];
        this.lastKnownMessageId = newMessages.at(-1).id;

        if (wasAtBottom) this.scrollToBottom();
      } catch {
        // A blip here just means we try again on the next tick - not worth
        // surfacing to the user or letting it become an unhandled rejection.
      } finally {
        this.polling = false;
      }
    },
    // Generous threshold - "close enough" to the bottom that a poll should
    // auto-follow new content, used when deciding whether to auto-scroll.
    isAtBottom() {
      const el = this.$refs.scrollArea;
      if (!el) return true;

      return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },
    // Auto-fires on scroll-to-top, no button — older messages just appear
    // above, with scroll position preserved so the view doesn't jump.
    onScroll() {
      const el = this.$refs.scrollArea;
      if (el && el.scrollTop < 80) this.loadEarlier();
    },
    // Only typing a reply or sending one dismisses the "New messages"
    // divider — not scrolling or clicking elsewhere.
    onTyping() {
      this.dismissDivider();
    },
    // Fades out first, then actually removes it from the timeline once the
    // transition finishes, rather than vanishing instantly.
    dismissDivider() {
      if (this.newMessagesDividerId === null || this.dividerFading) return;

      this.dividerFading = true;
      this.dividerFadeTimer = setTimeout(() => {
        this.newMessagesDividerId = null;
        this.dividerFading = false;
        this.dividerFadeTimer = null;
      }, 400);
    },
    async loadEarlier() {
      if (!this.hasMore || this.loadingEarlier) return;

      const el = this.$refs.scrollArea;
      const previousScrollHeight = el?.scrollHeight ?? 0;

      this.loadingEarlier = true;
      try {
        const { data } = await messagingApi.fetchThread(
          this.conversationToken,
          {
            before: this.nextBefore
          }
        );
        this.messages = [...data.messages, ...this.messages];
        this.hasMore = data.hasMore;
        this.nextBefore = data.nextBefore;

        this.$nextTick(() => {
          if (el) el.scrollTop = el.scrollHeight - previousScrollHeight;
        });
      } finally {
        this.loadingEarlier = false;
      }
    },
    onSent(message) {
      this.messages.push(message);
      this.draft = null;
      this.lastKnownMessageId = message.id;
      this.dismissDivider();
      this.scrollToBottom();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scrollArea;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    isSelf(message) {
      return message.senderId === this.currentUserId;
    },
    showDateDivider(index) {
      if (index === 0) return true;
      const prev = moment(this.messages[index - 1].createdAt);
      const current = moment(this.messages[index].createdAt);
      return !prev.isSame(current, "day");
    },
    formatDateDivider(value) {
      const date = moment(value);
      if (date.isSame(moment(), "day")) return "Today";
      if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
      return date.format("MMMM D");
    },
    formatTime(value) {
      return moment(value).format("MMM D, h:mma");
    },
    initials(name) {
      return (name || "")
        .split(" ")
        .map(part => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },
    attachmentIcon(fileType) {
      const icons = {
        "image/jpeg": "fa-solid fa-file-image",
        "image/png": "fa-solid fa-file-image",
        "application/pdf": "fa-solid fa-file-pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          "fa-solid fa-file-word",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          "fa-solid fa-file-excel"
      };
      return icons[fileType] || "fa-solid fa-file";
    }
  }
};
</script>

<style lang="scss" scoped>
@use "../../styles/base/variables" as *;

.thread-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.thread-view__header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.product-chip {
  border-color: $uwill-message-pill !important;
  color: $uwill-neutral-500 !important;
  background-color: $uwill-message-pill !important;
  min-height: auto;
}

.thread-view__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.thread-view__date-divider {
  text-align: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 12px 0;
}

.thread-view__new-divider {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: $uwill-peri;
  margin: 16px 0;
  opacity: 1;
  transition: opacity 0.4s ease;

  &--fading {
    opacity: 0;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid $uwill-peri-50;
  }

  &::before {
    margin-right: 8px;
  }

  &::after {
    margin-left: 8px;
  }
}

// One avatar + one shared timestamp per consecutive-same-sender group,
// matching the mock — not repeated per bubble.
.thread-view__group {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 16px;

  &--self {
    flex-direction: row-reverse;
  }
}

.thread-view__group-avatar {
  flex-shrink: 0;
  margin-bottom: 20px; // aligns with the last bubble, above its timestamp
}

.thread-view__group-body {
  display: flex;
  flex-direction: column;
  max-width: 70%;
  align-items: flex-start;
}

.thread-view__group--self .thread-view__group-body {
  align-items: flex-end;
}

.thread-view__bubble {
  padding: 10px 14px;
  border-radius: 16px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 4px;

  &--self {
    background-color: $uwill-peri;
    border: none;
    color: #fff;
  }
}

.thread-view__attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: $uwill-peri-50;
  width: fit-content;
  max-width: 100%;
}

.thread-view__attachment-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $uwill-peri;
  color: #fff;
}

.thread-view__attachment-filename {
  color: $uwill-peri;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-view__timestamp {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
